<template>
  <b-modal
    id="modalUsersInfo"
    v-model="localIsActive"
    centered
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    header-bg-variant="dark"
    header-text-variant="light"
    @hidden="closeModal"
  >
    <template #modal-title>
      {{ $tc("hanwha_dvr.groups", 2) }} DVR {{ hanwhaDvr.name }}
    </template>

    <groups-form
      v-if="showForm"
      ref="refGroupsForm"
      :channels="channels"
      :hanwha-dvr="hanwhaDvr"
      :item-data="itemData"
      style="height: 71vh; overflow-y: auto;"
    />

    <groups-list
      v-if="!showForm"
      :hanwha-dvr="hanwhaDvr"
      :loading="loading"
      :groups-info="groupsInfo"
      @edit="edit"
      @refresh="getGroups()"
      @show-form="create()"
      style="height: 71vh; overflow-y: auto;"
    />

    <template #modal-footer="{ ok }">
      <!-- Form Groups buttons -->
      <b-button
        v-if="showForm"
        class="text-capitalize"
        variant="secondary"
        :disabled="loading"
        @click="showForm = false"
      >
        {{ $t('actions.cancel') }}
      </b-button>
      <b-button
        v-if="showForm"
        variant="primary"
        :disabled="loading"
        @click="saveGroup"
      >
        {{ buttonSubmitText }}
      </b-button>
      <!-- Change password buttons -->
      <b-button
        v-if="!showForm"
        variant="primary"
        :disabled="loading"
        @click="ok()"
      >
        OK
      </b-button>
    </template>

  </b-modal>
</template>

<script>
import { BModal, BSpinner } from "bootstrap-vue";
import { computed, ref, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import axios from "@axios";
import vSelect from "vue-select";
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import GroupsForm from "./GroupsForm/Index.vue";
import GroupsList from "./GroupsList.vue";

export default {
  components: {
    BModal,
    BSpinner,
    vSelect,
    SkeletonLoading,
    GroupsForm,
    GroupsList,
  },
  props: {
    hanwhaDvr: { type: Object, require: true },
    isModalGroupsActive: { type: Boolean, require: true },
  },
  setup(props, { emit }) {
    //HOOKS
    onMounted(async () => {
      await getChannels();
      await getGroups();
    });

    //COMPOSABLES
    const toast = useToast();

    //DATA
    const channels = ref([]);

    const groupsInfo = ref([]);

    const loading = ref(false);

    const itemData = ref({
      BackupChannel: [],
      DeviceMenuAccess: [],
      EventMenuAccess: [],
      RecordMenuAccess: [],
      SearchChannel: [],
      SystemMenuAccess: [],
      NetworkMenuAccess: [],
      AlarmOutputAccess: false,
      RecordStartAccess: false,
      RecordStopAccess: false,
      PTZAccess: false,
      ShutdownAccess: false,
      isEdit: false
    });

    const refGroupsForm = ref(null);

    const showForm = ref(false);

    //COMPUTED
    const localIsActive = computed({
      get() {
        return props.isModalGroupsActive;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    const buttonSubmitText =  computed(() => (itemData.value.isEdit) ? 'Editar' : 'Guardar');

    //METHODS
    const checkArrayValues = (values) => {
      const valuesLength  = values.length;
      const [firstValue] = values;

      if ((valuesLength === 1) && (firstValue == 'None')) {
        return [];
      }

      return values;
    }

    const closeModal = () => {
      emit('close-modal');
    }

    const create = () => {
      showForm.value = true;

      itemData.value = {
        isEdit: false,
        BackupChannel: [],
        DeviceMenuAccess: [],
        EventMenuAccess: [],
        RecordMenuAccess: [],
        SearchChannel: [],
        SystemMenuAccess: [],
        NetworkMenuAccess: [],
        RecordStartAccess: false,
        RecordStopAccess: false,
        PTZAccess: false,
        AlarmOutputAccess: false,
        ShutdownAccess: false,
      }
    }

    const edit = (group) => {
      itemData.value.user_group_id     = group.UserGroupID;
      itemData.value.BackupChannel     = checkArrayValues(group.BackupChannel);
      itemData.value.DeviceMenuAccess  = checkArrayValues(group.DeviceMenuAccess);
      itemData.value.EventMenuAccess   = checkArrayValues(group.EventMenuAccess);
      itemData.value.RecordMenuAccess  = checkArrayValues(group.RecordMenuAccess);
      itemData.value.SearchChannel     = checkArrayValues(group.SearchChannel);
      itemData.value.SystemMenuAccess  = checkArrayValues(group.SystemMenuAccess);
      itemData.value.NetworkMenuAccess = checkArrayValues(group.NetworkMenuAccess);
      itemData.value.RecordStartAccess = group.RecordStartAccess;
      itemData.value.RecordStopAccess  = group.RecordStopAccess;
      itemData.value.PTZAccess         = group.PTZAccess;
      itemData.value.AlarmOutputAccess = group.AlarmOutputAccess;
      itemData.value.ShutdownAccess    = group.ShutdownAccess;
      itemData.value.isEdit            = true;

      showForm.value = true;
    }

    async function fetchData(params) {
      return await axios.post('/v1/hanwha/dvr/admin/users', params);
    }

    const getChannels = async () => {
      const params = {
        action: 'read-channels',
        id_dvr: props.hanwhaDvr.id,
        params: {}
      }

      loading.value  = true;

      await axios.post('/v1/hanwha/dvr/admin/users', params)
        .then(response => {
          const { data } = response;

          channels.value = data.data.RecordSetup;
        })
        .catch(error => {
          channels.value = [];
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const getGroups = async () => {
      const params = {
        action: 'read-groups',
        id_dvr: props.hanwhaDvr.id,
        params: {}
      }

      showForm.value = false;
      loading.value  = true;

      await fetchData(params)
        .then(response => {
          const { data } = response;

          groupsInfo.value = data.data.UserGroups;
        })
        .catch(error => {
          groupsInfo.value = [];
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const saveGroup = async () => {
      const isValid = await refGroupsForm.value.validateStep();

      if (!isValid) {
        return;
      }

      const params = {
        action: (itemData.value.isEdit) ? 'update-group' : 'create-group',
        id_dvr: props.hanwhaDvr.id,
        params: itemData.value
      }

      loading.value = true;

      await fetchData(params)
        .then(response => {
          const { data } = response;

          if (data.data.Response === 'Fail') {
            const { Code,Details } = data.data.Error;

            toast({
              component: ToastificationContent,
              props: {
                title: "Validation error",
                text: `${Code} - ${Details}`,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });

            loading.value = false;

            return;
          }

          toast({
              component: ToastificationContent,
              props: {
                title: 'Ok',
                text: data.data.Response,
                icon: "CheckIcon",
                variant: "success",
              },
            });

          loading.value = false;

         (async () => await getGroups())();
        })
        .catch(error => {
          loading.value = false;
        });
    }

    return {
      channels,
      groupsInfo,
      loading,
      itemData,
      refGroupsForm,
      showForm,

      localIsActive,
      buttonSubmitText,

      closeModal,
      create,
      edit,
      getGroups,
      saveGroup
    };
  },
};
</script>

<style lang="scss" scoped>
.diskInfoDetailKey {
  width: 140px;
  font-weight: 100;
  color: #a5a5a5;
}
.diskInfoDetailItem {
  color: #393735;
}
.dark-layout {
  .diskInfoDetailKey {
    color: #a5a5a5;
  }
  .diskInfoDetailItem {
    color: #fff;
  }
}
</style>
